import  { useState, useEffect , useRef} from 'react';

import {
 
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList,
  LineChart, Line, CartesianGrid
} from 'recharts';

import railCoverImage from '../assets/images/railcover.png';


// --- Reusable Components (in-file) ---

// Card component for charts and logs
const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title }) => (
  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 px-6 py-4">
    {title}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

// --- App Components ---



const TruncatedText = ({ text, limit = 50 }) => {
  if (!text) return '-';
  const words = text.split(' ');
  if (words.length <= limit) {
    return text;
  }
  const truncated = words.slice(0, limit).join(' ') + '...';
  return <span title={text}>{truncated}</span>;
};




function SummaryCards({ data }) {
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {data && data.map((item) => (
        <div key={item.label} className="bg-gray-50 border border-gray-200 p-6 rounded-lg text-center shadow-sm">
          <p className={`text-4xl font-bold ${item.color || 'text-gray-900'}`}>{item.value}</p>
          <p className="text-sm text-gray-600 mt-1">{item.label}</p>
        </div>
      ))}
    </section>
  );
}

function OverallStatusChart({ data ,chartsReadyRef}) {
  const notified = useRef(false);
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    // Calculate the position for the label (slightly outside the pie slice)
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5; // mid-radius
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const name = data[index].name;

    if (percent * 100 > 5) { // Only show label if slice is big enough
      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          dominantBaseline="central"
          className="text-xs font-semibold"
        >
          
          <tspan x={x} dy="1.2em">{`${(percent).toFixed(0)}`}</tspan>
        </text>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader title="Overall Status Distribution" />
      <CardContent className="flex flex-col flex-row items-center justify-center gap-4 py-4">
        <ResponsiveContainer width="100%" height={200}
        onResize={() => {
        if (!notified.current) {
          chartsReadyRef.current += 1;
          notified.current = true;
        }
      }}
      >
          <PieChart>
            <Pie
               isAnimationActive={false} 
              data={data}
              cx="50%"
              cy="55%"
              innerRadius={60}
              outerRadius={80}
              fill="#8884d8"
              paddingAngle={2}
              dataKey="value"
              
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name, props) => [`${props.payload.percent}% (${value})`, name]} />
          </PieChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="space-y-2 w-full max-w-[150px]">
          {data.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                <span className="w-3 h-3 rounded-sm mr-2" style={{ backgroundColor: item.color }}></span>
                <span className="font-medium" style={{ color: item.color }}>
                  {item.name}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-900 font-semibold">{item.percent}%</span>
                <span className="ml-1 text-gray-500">({item.value})</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PolicyTypeChart({ data ,chartsReadyRef}) {
  const notified = useRef(false);
  return (
    <Card>
      <CardHeader title="Block Policy Type Distribution" />
      <CardContent className="h-64 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%"
        onResize={() => {
        if (!notified.current) {
          chartsReadyRef.current += 1;
          notified.current = true;
        }
      }}
      >
          <BarChart
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="name" axisLine={false} tickLine={false} padding={{ left: 20, right: 20 }} />
            <YAxis hide domain={[0, 10]} /> {/* Adjust domain based on max value */}
            <Tooltip />
            <Bar  isAnimationActive={false}  dataKey="value" radius={[5, 5, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fontWeight: 'bold' }} />
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function ActivityTrendChart({ data ,chartsReadyRef}) {
  const notified = useRef(false);
  return (
    <Card>
      <CardHeader title="7-Day Activity Trend (Queries vs Blocks)" />
      <CardContent className="h-80 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%"
        onResize={() => {
        if (!notified.current) {
          chartsReadyRef.current += 1;
          notified.current = true;
        }
      }}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis dataKey="name" tickLine={false} axisLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Legend />
            <Line  isAnimationActive={false}  type="monotone" dataKey="queries" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            <Line isAnimationActive={false} type="monotone" dataKey="blocks" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function BlockReasonsChart({ data }) {
  return (
    <Card>
      <CardHeader title="Top 8 Block Reasons" />
      <CardContent className="space-y-4 h-80 overflow-y-auto">
        {data.map((item) => (
          <div key={item.reason}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">{item.reason}</span>
              <span className="text-gray-500">{item.percent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-3">
                <div
                  className={`${item.color} h-3 rounded-full`}
                  style={{ width: `${item.percent}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-700 w-6 text-right">{item.count}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AuditLog({ data }) {
  return (
    <Card>
      <CardHeader title="Detailed Audit Log" />
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prompt
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Response
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Block Reason
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, idx) => (
              <tr key={idx}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.created_at}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    row.status === 'PASS'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {row.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.prompt} /></td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.response} /></td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs"><TruncatedText text={row.reason} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


const CoverPage = ({ FilterDate }) => {
  const currentDate = new Date().toLocaleDateString();
  return (
    <div id="cover-container"> <style>{`
    
      @page:first {
          margin: 0;          
        }

     
   
      .cover-page {
          height: 100vh;
          
          
          background-size: cover;
          background-position: center;
          display: flex;
          flex-direction: column;
          justify-content: top;
          align-items: center;
          page-break-after: always;
          text-align: center;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .cover-title {
          font-size: 60px;
          font-weight: bold;
          margin-bottom: 30px;          
          margin-left: 20px;
          margin-right: 20px;
          margin-top: 200px;
          color: #ffffff;
        }
        .cover-date {
          font-size: 30px;
          color: #ffffff;
        }
     
    `} </style> 
     

    <div  className="cover-page" style={{ backgroundImage: `url(${railCoverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="cover-title">AI Shield Cases and Records</div>
      <div className="cover-date">{FilterDate?.startDate} to  {FilterDate?.endDate}</div>
    </div>
    </div>
  );
}


// --- Main App Component ---
export default function PrintView({chartsReadyRef, printData}) {
  
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (printData) {
      setReady(true);
       // notify parent if needed
    }
  }, [printData]);

  if (!ready) return <div>Loading…</div>;


  return (
    <div className="print-wrapper">
      <style>{`
        @page {
          size: A4;
          margin: 10mm;
        }
        
        @media print {
      #aa {
        zoom:0.5;
      }
    }

        @media screen {
          .print-wrapper{
            
            position: absolute; top: -99999px;visibility: hidden;
          }
        }

          
        @media print {

         .grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 1rem !important;
            page-break-inside: avoid;
          }

  .grid > * {
    min-width: 0;
    word-break: break-word;
  }
  #summary-card > .grid{
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr !important;
    column-gap: 8mm;
  }

  /* CRITICAL: prevent content forcing collapse */
  #summary-card > .grid > * {
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .flex-col {
        display: flex !important;
        flex-direction: column !important;
      }

      .flex-row {
        flex-direction: row !important;
        flex-wrap: nowrap !important; /* Prevent wrapping */
      }
        [class*="gap-"] {
        gap: var(--tw-gap) !important;
      }
      .gap-8{
        gap: 8mm !important;
      }
      .gap-6{
        gap: 6mm !important;
      }
      .gap-4{
        gap: 4mm !important;
      }
      .gap-2{
        gap: 2mm !important;
      }
      .gap-1{
        gap: 1mm !important;
      }
    }

    td {
  word-break: break-word;  /* for very long words or Unicode text */
  white-space: normal;     /* allow wrapping */
}
        
      `}</style>    
         

      <CoverPage FilterDate={printData.filtering} />

       <div id="aa" className="p-4 sm:p-8 max-w-[1440px] mx-auto  min-h-screen space-y-6">
       
        <div id="summary-card" className="space-y-6">
          {/* <Header /> */}     
        
          <SummaryCards data={printData.summaryData} />
        </div>
        
        
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OverallStatusChart data={printData.statusDistributionRechartsData} chartsReadyRef={chartsReadyRef} />
          <PolicyTypeChart data={printData.blockPolicyTypeData} chartsReadyRef={chartsReadyRef} />
          <ActivityTrendChart data={printData.activityTrendData} chartsReadyRef={chartsReadyRef} />
          <BlockReasonsChart data={printData.blockReasonsData} />
        </section>

     
       
        <Card>
          <AuditLog data={printData.displayedAuditLog} />
        
        </Card>
      </div>
    </div>
  );
}
